import { memo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AiOutlineHeart, AiFillHeart } from "react-icons/ai";
import { useWishlist } from "../../contexts/WishlistContext";
import { useWishPop } from "../../hooks/useWishPop";
import { withViewTransition } from "../../utils/viewTransition";
import { setMorphCandidate, morphNameFor } from "../../utils/productMorph";
import "./DiscoveryCard.css";
import { imageProps, SIZES } from "../../utils/imageUrl";

/**
 * Recommendation card for the homepage Discovery panel: a light frame
 * holding the product photo whole (never cropped), and a dark panel below
 * carrying only what identifies it — the product's name. No gradient over
 * the product, and no rating, badge, description, price or buy button; the
 * panel is a row of recommendations, not a checkout.
 *
 * The star row was removed from every product card on 2026-09-20 (see
 * CLAUDE.md). Ratings remain on the product detail page.
 *
 * Badges are deliberately absent here (they remain on ProductCard and
 * everywhere else). Evidence badges live only on the /spotlight response,
 * while a card is rendered from product-detail data whenever the period's
 * batch is read back from storage — so a badge would show on the draw and
 * vanish on the next reload, within one recommendation period.
 *
 * The whole card opens the product page, but the only link is the product
 * name inside the heading, stretched over the card by a CSS overlay
 * (.dcard-link::after) — the accessible "card" pattern, so a screen reader
 * hears the name once, not repeated by a card-wide link's content. The
 * wishlist heart is a sibling layered above it (never inside the <a>), so
 * activating the heart can never also navigate.
 *
 * Opening a card is plain navigation: it records nothing and retires
 * nothing. Which three products appear is decided by the schedule
 * (src/utils/discoveryRotation.js), not by what was clicked.
 */
const DiscoveryCard = ({ product }) => {
  const { id, name, slug, image_url } = product;
  const productPath = `/product/${slug ?? id}`;
  const navigate = useNavigate();
  const imgRef = useRef(null);
  const { isWishlisted, toggleWishlist } = useWishlist();
  const wishlisted = isWishlisted(id);
  const [popping, pop] = useWishPop();

  // Same navigation as ProductCard's image link: modified clicks fall
  // through to the browser's native new-tab behavior; a plain click gets
  // the shared-element morph into the product page.
  const handleClick = (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;  // new tab: not our navigation
    e.preventDefault();
    if (imgRef.current) imgRef.current.style.viewTransitionName = morphNameFor(id);
    setMorphCandidate(id);
    withViewTransition(() => navigate(productPath));
  };

  const handleWishlist = (e) => {
    e.stopPropagation();
    if (!wishlisted) pop();   // only on the way in — see useWishPop
    toggleWishlist(product);
  };

  return (
    <li className="dcard">
      <div className="dcard-surface">
        <div className={`dcard-media${image_url ? "" : " dcard-media--empty"}`}>
          {image_url && (
            <img ref={imgRef} className="dcard-img" {...imageProps(image_url, "product", SIZES.card)} alt={name} loading="lazy" decoding="async" />
          )}
        </div>
        <div className="dcard-info">
          <h3 className="dcard-name">
            <Link to={productPath} className="dcard-link" onClick={handleClick}>{name}</Link>
          </h3>
        </div>
      </div>

      <button
        type="button"
        className={`dcard-wish${wishlisted ? " is-active" : ""}${popping ? " is-popping" : ""}`}
        aria-pressed={wishlisted}
        aria-label={wishlisted ? `Remove ${name} from wishlist` : `Add ${name} to wishlist`}
        onClick={handleWishlist}
      >
        {wishlisted ? <AiFillHeart aria-hidden="true" /> : <AiOutlineHeart aria-hidden="true" />}
      </button>
    </li>
  );
};

// Memoised (speed brief item 26): a grid or rail redraws when the cart or a
// filter changes, and a card whose product and handlers are unchanged has
// nothing new to draw.
export default memo(DiscoveryCard);
